import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

interface OnboardingProgressState {
  currentStep: number;
  totalSteps: number;
  hideProgressBar: boolean;
}

interface OnboardingProgressContextValue extends OnboardingProgressState {
  setProgress: (
    currentStep: number,
    totalSteps: number,
    options?: { hideProgressBar?: boolean }
  ) => void;
}

const defaultState: OnboardingProgressState = {
  currentStep: 1,
  totalSteps: 5,
  hideProgressBar: true,
};

const OnboardingProgressContext = createContext<
  OnboardingProgressContextValue | undefined
>(undefined);

export function OnboardingProgressProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<OnboardingProgressState>(defaultState);
  const setProgress = useCallback(
    (
      currentStep: number,
      totalSteps: number,
      options?: { hideProgressBar?: boolean }
    ) => {
      setState((prev) => ({
        currentStep,
        totalSteps,
        hideProgressBar:
          options?.hideProgressBar ?? prev.hideProgressBar,
      }));
    },
    []
  );

  return (
    <OnboardingProgressContext.Provider
      value={{ ...state, setProgress }}
    >
      {children}
    </OnboardingProgressContext.Provider>
  );
}

export function useOnboardingProgress(): OnboardingProgressContextValue {
  const value = useContext(OnboardingProgressContext);
  if (value === undefined) {
    throw new Error(
      "useOnboardingProgress must be used within OnboardingProgressProvider"
    );
  }
  return value;
}
